import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
} from "@mui/material";
import {
  Business as BusinessIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import AdminPage from "./AdminPage";
import { apiClient } from "../utils/apiClient";
import { openDatabaseObjectModal } from "../store/adminSlice";

interface UserInfo {
  id: string;
  email: string;
  createdAt: string;
  hasConnectedEmail: boolean;
  organizationName?: string;
}

interface OrganizationInfo {
  id: string;
  name: string;
}

export function AdminListUsersPage() {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [organizations, setOrganizations] = useState<OrganizationInfo[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<UserInfo | null>(null);
  const [assignOrgDialogOpen, setAssignOrgDialogOpen] = useState(false);
  const [selectedOrgId, setSelectedOrgId] = useState<string>("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    fetchUsers();
    fetchOrganizations();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await apiClient.post<{ users: UserInfo[] }>(
        `/admin/list_users`,
        {}
      );
      const sortedUsers = res.users.sort(
        (a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ); // Order by last created
      setUsers(sortedUsers);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const res = await apiClient.post<{ organizations: OrganizationInfo[] }>(
        `/admin/organizations/list`,
        {}
      );
      setOrganizations(res.organizations);
    } catch (error) {
      console.error("Error fetching organizations:", error);
    }
  };

  const checkEmailConnection = async (email: string) => {
    try {
      const res = await apiClient.post(`/admin/test_use_email_connection`, {
        email,
      });
      console.log(`Email check for ${email}:`, res);
    } catch (error) {
      console.error(`Error checking email connection for ${email}:`, error);
    }
  };

  const handleAssignOrganization = async () => {
    if (!selectedUser) return;
    
    try {
      await apiClient.post(`/admin/organizations/assign-user`, {
        userId: selectedUser.id,
        organizationId: selectedOrgId || null,
      });
      
      // Refresh users list
      await fetchUsers();
      setAssignOrgDialogOpen(false);
      setSelectedUser(null);
      setSelectedOrgId("");
    } catch (error) {
      console.error("Error assigning organization:", error);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      await apiClient.delete(`/admin/delete_user`, {
        data: { userId: selectedUser.id },
      });
      
      // Refresh users list
      await fetchUsers();
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error("Error deleting user:", error);
      alert("Failed to delete user. Please try again.");
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: UserInfo) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const openAssignOrgDialog = () => {
    setSelectedOrgId(organizations.find(org => org.name === selectedUser?.organizationName)?.id || "");
    setAssignOrgDialogOpen(true);
    handleMenuClose();
  };

  const openDeleteDialog = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  return (
    <AdminPage>
      <Box sx={{ padding: "20px" }}>
        <Box sx={{ display: "flex", alignItems: "center", marginBottom: "16px" }}>
          <Typography variant="h4" sx={{ flexGrow: 1 }}>
            User Management
          </Typography>
          <Button
            variant="outlined"
            startIcon={<BusinessIcon />}
            onClick={() => navigate("/admin/organizations")}
          >
            Manage Organizations
          </Button>
        </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Email</strong>
              </TableCell>
              <TableCell>
                <strong>User ID</strong>
              </TableCell>
              <TableCell>
                <strong>Organization</strong>
              </TableCell>
              <TableCell>
                <strong>Created At</strong>
              </TableCell>
              <TableCell>
                <strong>Connected Email</strong>
              </TableCell>
              <TableCell>
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id} sx={{ cursor: "pointer" }}>
                <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>{user.email}</TableCell>
                <TableCell>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => dispatch(openDatabaseObjectModal({ table: "user", id: user.id }))}
                    sx={{
                      fontFamily: "monospace",
                      fontSize: "0.75rem",
                      cursor: "pointer",
                    }}
                  >
                    {user.id.slice(0, 8)}...
                  </Link>
                </TableCell>
                <TableCell sx={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  {user.organizationName ? (
                    <Chip 
                      label={user.organizationName} 
                      size="small" 
                      variant="outlined"
                      sx={{ whiteSpace: "normal", height: "auto", "& .MuiChip-label": { whiteSpace: "normal" } }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      None
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  {new Date(user.createdAt).toLocaleString()}
                </TableCell>
                <TableCell>{user.hasConnectedEmail ? "✔️" : ""}</TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {user.hasConnectedEmail && (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => checkEmailConnection(user.email)}
                      >
                        Check Email
                      </Button>
                    )}
                    <IconButton 
                      size="small"
                      onClick={(e) => handleMenuClick(e, user)}
                    >
                      <MoreVertIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={openAssignOrgDialog}>
          Assign Organization
        </MenuItem>
        <MenuItem onClick={openDeleteDialog} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1, fontSize: 20 }} />
          Delete User
        </MenuItem>
      </Menu>

      {/* Assign Organization Dialog */}
      <Dialog
        open={assignOrgDialogOpen}
        onClose={() => setAssignOrgDialogOpen(false)}
      >
        <DialogTitle>Assign Organization</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Assign {selectedUser?.email} to an organization
          </Typography>
          <FormControl fullWidth>
            <Select
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value as string)}
              displayEmpty
            >
              <MenuItem value="">
                <em>No Organization</em>
              </MenuItem>
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignOrgDialogOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleAssignOrganization} variant="contained">
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete User</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete user <strong>{selectedUser?.email}</strong>?
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
            This action will permanently delete:
          </Typography>
          <Typography variant="body2" component="ul" sx={{ ml: 2 }}>
            <li>The user account</li>
            <li>All authentication tokens</li>
            <li>All extracted data associated with this user</li>
            <li>All action logs</li>
            <li>All emails</li>
            <li>All recurring workflows</li>
          </Typography>
          <Typography variant="body2" color="error.main" sx={{ mt: 2, fontWeight: 'bold' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteUser} 
            variant="contained" 
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </AdminPage>
  );
}
